import WaveSurfer from './wavesurfer.esm.js';

/**
 * Global demo-sample audio player.
 * One WaveSurfer instance, lazily created on first play. Any element with
 * [data-demo-play] + [data-sample-url] anywhere on the page (product page
 * "Play Now" button, product-card play icon, etc.) can trigger it.
 * Multiple triggers for the same product (e.g. the button on the product
 * page and a card for the same product in a "More From This Label" row)
 * stay in sync via data-product-id.
 */

class DemoAudioPlayer {
  constructor() {
    this.bar = document.querySelector('[data-demo-player]');
    if (!this.bar) return;

    this.waveformEl = this.bar.querySelector('[data-demo-player-waveform]');
    this.titleEl = this.bar.querySelector('[data-demo-player-title]');
    this.artworkEl = this.bar.querySelector('[data-demo-player-artwork]');
    this.toggleEl = this.bar.querySelector('[data-demo-player-toggle]');
    this.closeEl = this.bar.querySelector('[data-demo-player-close]');
    this.currentTimeEl = this.bar.querySelector('[data-demo-player-current-time]');
    this.durationEl = this.bar.querySelector('[data-demo-player-duration]');

    this.wavesurfer = null;
    this.activeProductId = null;

    document.addEventListener('click', (event) => this.handleDocumentClick(event));
    this.toggleEl?.addEventListener('click', () => this.wavesurfer?.playPause());
    this.closeEl?.addEventListener('click', () => this.close());
  }

  handleDocumentClick(event) {
    const trigger = event.target.closest('[data-demo-play]');
    if (!trigger) return;

    event.preventDefault();
    const sampleUrl = trigger.getAttribute('data-sample-url');
    if (!sampleUrl) return;

    const productId = trigger.getAttribute('data-product-id');

    if (productId && productId === this.activeProductId && this.wavesurfer) {
      this.wavesurfer.playPause();
      return;
    }

    this.loadTrack({
      sampleUrl,
      productId,
      title: trigger.getAttribute('data-title') || '',
      artwork: trigger.getAttribute('data-artwork') || '',
    });
  }

  ensureWavesurfer() {
    if (this.wavesurfer) return this.wavesurfer;

    this.wavesurfer = WaveSurfer.create({
      container: this.waveformEl,
      height: 32,
      barWidth: 2,
      barGap: 2,
      barRadius: 1,
      cursorWidth: 0,
      waveColor: '#C4B8A0',
      progressColor: '#111111',
    });

    this.wavesurfer.on('play', () => this.setPlayingState(true));
    this.wavesurfer.on('pause', () => this.setPlayingState(false));
    this.wavesurfer.on('finish', () => this.setPlayingState(false));
    this.wavesurfer.on('audioprocess', () => this.updateTime());
    this.wavesurfer.on('ready', () => {
      this.updateTime();
      this.wavesurfer.play();
    });

    return this.wavesurfer;
  }

  loadTrack({ sampleUrl, productId, title, artwork }) {
    this.activeProductId = productId || null;
    this.titleEl.textContent = title;
    if (this.artworkEl) {
      if (artwork) {
        this.artworkEl.style.backgroundImage = `url("${artwork}")`;
        this.artworkEl.hidden = false;
      } else {
        this.artworkEl.hidden = true;
      }
    }

    this.bar.hidden = false;
    this.bar.setAttribute('data-state', 'open');

    this.syncTriggerButtons();
    this.ensureWavesurfer().load(sampleUrl);
  }

  setPlayingState(isPlaying) {
    this.bar.classList.toggle('is-playing', isPlaying);
    this.syncTriggerButtons();
  }

  syncTriggerButtons() {
    document.querySelectorAll('[data-demo-play]').forEach((button) => {
      const isActiveProduct = button.getAttribute('data-product-id') === this.activeProductId;
      const isPlaying = isActiveProduct && this.bar.classList.contains('is-playing');
      button.classList.toggle('is-playing', isPlaying);
      button.setAttribute('aria-pressed', String(isPlaying));
    });
  }

  updateTime() {
    if (!this.wavesurfer) return;
    this.currentTimeEl.textContent = this.formatTime(this.wavesurfer.getCurrentTime());
    this.durationEl.textContent = this.formatTime(this.wavesurfer.getDuration());
  }

  formatTime(seconds) {
    if (!seconds || Number.isNaN(seconds)) return '00:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  }

  close() {
    this.wavesurfer?.pause();
    this.activeProductId = null;
    this.bar.hidden = true;
    this.bar.removeAttribute('data-state');
    this.syncTriggerButtons();
  }
}

document.addEventListener('DOMContentLoaded', () => new DemoAudioPlayer());